import { IChangePasswordParams, ILoginCredentials } from "@multiweb/clients/auth";
import { ISectionLogger } from "@multiweb/logging";
import { ILanguage, ILanguageExtended } from "@multiweb/types/contracts";
import { DeviceInfo } from "../../../../Device/Info/DeviceInfo";
import { EOWLogonError } from "../../../../OW.Remoting/Basic/OWExceptions";
import { IAuthManager } from "../../../../OW.Remoting/Remoting/Auth/IAuthManager";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { PasswordChangeBuilder, PasswordChangeBuilderInputType } from "../../UI/DataBuilders/PasswordChangeBuilder";
import { StringBuilder } from "../../UI/DataBuilders/StringBuilder";
import { MessageButtonType, MessageWindow } from "../../UI/MessageWindow";
import { FormBase } from "./FormBase";
import { ILoginCredentialsProvider } from "./ILoginCredentialsProvider";
import { LoginStorage } from "./LoginStorage";
import { NavigationService } from "./NavigationService";

export class LoginForm extends FormBase implements ILoginCredentialsProvider {
    //#region Fields
    private _authManager: IAuthManager;
    private _onSuccessfulLogin: () => any;
    private _defaultDomain: string;
    private _logger: ISectionLogger;
    private _passwordContainer: HTMLDivElement;
    //#endregion Fields

    constructor(
        authManager: IAuthManager,
        _navigator: NavigationService,
        languages: readonly ILanguageExtended[],
        onSuccessfulLogin: () => any,
        defaultDomain: string | undefined,
        logger: ISectionLogger,
    ) {
        super();

        this._logger = logger;
        this._authManager = authManager;
        this._onSuccessfulLogin = onSuccessfulLogin;
        this._defaultDomain = defaultDomain;

        this._initializeComponents(languages);
    }

    //#region Methods
    public getUsername(withDefaultDomain: boolean = true): string {
        return (this._defaultDomain && withDefaultDomain) ? this._defaultDomain + "\\" + LoginForm._getUsernameElement().value : LoginForm._getUsernameElement().value;
    }

    public getPassword(): string {
        return LoginForm._getPasswordElement().value;
    }

    private _getPasswordInputContainer(): HTMLDivElement | null {
        if (!this._passwordContainer) {
            this._passwordContainer = document.querySelector("[data-tsccontrol='PasswordEdit']")?.parentElement as HTMLDivElement;
        }
        return this._passwordContainer;
    }

    private _setPasswordContainerVisibility(visible: boolean): void {
        const passwordElement = this._getPasswordInputContainer();
        if (passwordElement) {
            (visible) ? passwordElement.style.display = "" : passwordElement.style.display = "none";
        }
    }

    public getLanguage(): string {
        return LoginForm._getLanguage().code;
    }

    private static _getLanguage(): ILanguage {
        const selectedOption = document.querySelector("#lang-combo select > option:checked");
        return {
            code: selectedOption.getAttribute("data-language"),
            culture: selectedOption.getAttribute("data-culture"),
        };
    }

    protected override _getErrorElement(): HTMLElement {
        return document.getElementById("error-login-div");
    }

    protected override _getSubmitLoginElement(): HTMLInputElement {
        return document.getElementById("send-logon") as HTMLInputElement;
    }

    private _initializeComponents(languages: readonly ILanguageExtended[]): void {
        this._loadTextLocalizations();
        LoginForm._focusUsernameInput();
        LoginForm._setLastLoggedUserToUsernameInput();
        this._initializeLanguageComboBox(languages);
        this._registerChangeListenerForLangComboBox();
        this._registerAsyncClickEventListenerToElementById("send-logon", (e) => this._login(e));
        LoginForm._getUsernameElement().addEventListener("keyup", (e) => this._keyupListenerUsername(e));
        this._registerClickEventListenerToPwdChangeButton();
    }

    private _getLoginCredentials(): ILoginCredentials {
        return {
            userName: this.getUsername(),
            password: this.getPassword(),
            language: this.getLanguage(),
        };
    }

    private static _getUsernameElement(): HTMLInputElement {
        return (document.querySelector("#un > input") as HTMLInputElement);
    }

    private static _getPasswordElement(): HTMLInputElement {
        return (document.querySelector("#pw > input") as HTMLInputElement);
    }

    private static _getLanguageComboBox(): HTMLInputElement {
        return (document.querySelector("#lang-combo select") as HTMLInputElement);
    }

    private _loadTextLocalizations(): void {
        Localization.addLocData(this._getSubmitLoginElement(), "GUI-172655"); // Přihlásit
        Localization.addLocData(document.getElementById("pwd-change"), "GUI-678727"); // Změnit heslo
    }

    private static _focusUsernameInput(): void {
        if (!DeviceInfo.deviceProperties.touch)
            LoginForm._getUsernameElement().focus();
    }

    private static _setLastLoggedUserToUsernameInput(): void {
        const lastLoginUsername = LoginStorage.getLastLoginUsername();
        if (lastLoginUsername) {
            LoginForm._getUsernameElement().value = lastLoginUsername;
        }
    }

    private _initializeLanguageComboBox(languages: readonly ILanguageExtended[]): void {
        // ComboBox má po vytvoření implicitně 1 prázdný option, tak se smaže
        LoginForm._clearComboBoxOptions("lang-combo");
        LoginForm._fillComboBoxWithLanguages("lang-combo", languages);
    }

    private static _clearComboBoxOptions(comboBoxId: string): void {
        const combo: HTMLElement = document.getElementById(comboBoxId);
        const sel: Element = combo.getElementsByTagName("select")[0];

        sel.innerHTML = "";
    }

    private static _fillComboBoxWithLanguages(comboBoxId: string, languages: readonly ILanguageExtended[]): void {
        const combo: HTMLElement = document.getElementById(comboBoxId);
        const sel: HTMLSelectElement = combo.getElementsByTagName("select")[0];

        // nastaví jazyk na zapamatovaný ze session storage nebo na primární jazyk, pokud není uložený v session storage
        const rememberedLang = LoginStorage.getLastSelectedLoginLang();
        for (const lang of languages) {
            const opt = document.createElement("option");
            opt.textContent = lang.description;
            opt.setAttribute("data-culture", lang.culture);
            opt.setAttribute("data-language", lang.code);
            if ((rememberedLang == null && lang.isPrimary) || (rememberedLang != null && rememberedLang === lang.code))
                opt.setAttribute("selected", "selected");
            sel.appendChild(opt);
        }
        combo.dispatchEvent(new Event("change"));
    }

    /**
     * Registers an event listener for change event when a language changes
     * in the language ComboBox.
     */
    private _registerChangeListenerForLangComboBox(): void {
        // Změna jazyka pri prepnuti v comboboxu
        document.querySelector("#lang-combo").addEventListener("change", () => {
            const langInfo = LoginForm._getLanguage();
            LoginStorage.setLastSelectedLoginLang(langInfo.code);
            Localization.changeLanguage(langInfo.code, langInfo.culture);
        });
    }

    /**
     * Aktivuje/deaktivuje elementy formuláře
     * @param disabled
     */
    protected SetControlsDisabled(disabled: boolean): void {
        LoginForm._getUsernameElement().disabled = disabled;
        LoginForm._getPasswordElement().disabled = disabled;
        LoginForm._getLanguageComboBox().disabled = disabled;
    }

    /**
     * Funkce volana pri stisku tlacitka Prihlasit se
     * @parma e MouseEvent
     */
    private async _login(e: Event): Promise<void> {
        const targetElement = e.target as HTMLElement;
        const loginCredentials = this._getLoginCredentials();

        try {
            e.preventDefault();

            targetElement.classList.add("loading");
            this.SetControlsDisabled(true);

            this._clearError();

            if (loginCredentials.password === "" || loginCredentials.userName === "") {
                this._displayErrorFromCode("InvalidCredentials");
                return;
            }

            await this._authManager.login(loginCredentials, false);
            this._successful = true;
            this._onSuccessfulLogin();
        } catch (er) {
            if (er instanceof EOWLogonError) {
                if (er.code === "PasswordChangeRequired" || er.code === "UserMustChangePassword" || er.code === "PasswordExpired") {
                    this._setPasswordContainerVisibility(false);
                    this._changeFormStateToPasswordChange();
                }

                this._displayError(er);

                LoginForm._getPasswordElement().value = "";
                LoginForm._getPasswordElement().focus();
            } else {
                MessageWindow.showAlert(new StringBuilder(er.toString()));
            }
        } finally {
            this.SetControlsDisabled(false);
            targetElement.classList.remove("loading");
        }
    }

    private _keyupListenerUsername(_e: Event): void {
        this._changeFormStateToLogin();
        this._setPasswordContainerVisibility(true);
    }

    private _changeFormStateToPasswordChange(): void {
        document.getElementById("pwd-change").style.display = "";
        this._getSubmitLoginElement().style.display = "none";
    }

    private _changeFormStateToLogin(): void {
        document.getElementById("pwd-change").style.display = "none";
        this._getSubmitLoginElement().style.display = "";
        document.getElementById("error-login-div").innerHTML = "";
    }

    private _registerClickEventListenerToPwdChangeButton(): void {
        document.getElementById("pwd-change").addEventListener("click", async () => {
            const language = this.getLanguage();
            const username = this.getUsername();

            const changePwdBuilder = new PasswordChangeBuilder();
            const msgWindow = new MessageWindow(changePwdBuilder);

            msgWindow.okButtonCaption = "GUI-678496"; // Změnit
            msgWindow.cancelButtonCaption = "GUI-33412"; // Zrušit

            msgWindow.okCallback = async () => {
                if (changePwdBuilder.password == null) return false;

                try {
                    const changePasswordParams: IChangePasswordParams = {
                        userName: username,
                        oldPassword: changePwdBuilder.oldPassword,
                        newPassword: changePwdBuilder.password,
                        language,
                    };
                    const result = await this._authManager.changePassword(changePasswordParams);
                    if (!result.status) {
                        let errorText: string;
                        let errorInputType = undefined;
                        if (result.error.code === "PasswordChangeWrongOldPassword") {
                            errorText = result.error.message;
                            errorInputType = PasswordChangeBuilderInputType.oldPassword;
                        } else if (result.error.code && this._errorMessages.errorCodeLocalizationMap.has(result.error.code)) {
                            errorText = await Localization.getTextPromise(this._errorMessages.errorCodeLocalizationMap.get(result.error.code));
                        } else errorText = result.error.message;

                        changePwdBuilder.setInvalid(errorText, errorInputType);
                        return false;
                    }
                } catch (e) {
                    this._logger.log(e);

                    if (e.message) {
                        changePwdBuilder.setInvalid(e.message);
                    }
                    return false;
                }
                return true;
            };

            const result = await msgWindow.show();

            if (result.result == MessageButtonType.ok) {
                this._changeFormStateToLogin();
                this._setPasswordContainerVisibility(true);
                Localization.addLocData(document.getElementById("error-login-div"), "GUI-678729"); // Prosím proveďte přihlášení pomocí nového hesla.
            }
        });
    }

    //#endregion Methods
}
